import { Tabs } from "@repo/core/components/core/Tabs";
import { CardInfoView } from "@repo/core/screens/Proposals/screens/ProposalsViewInfo/components/CardInfoView";
import { ProposalDetails } from "./sections/ProposalDetails";
import { Badge, HStack, RefreshControl } from "@gluestack-ui/themed";
import { VStack } from "@gluestack-ui/themed";
import { DocumentsView } from "./sections/DocumentsView";
import { Button } from "@repo/core/components/core/Button";

import {
  canViewProposalAnalysisItems,
  canViewProposalDataTab,
  getProposalViewInfoTabs
} from "./utils";
import { AlertModal } from "@repo/core/components/layout/ModalTemplate/AlertModal";

import { LimitDetails } from "@repo/core/screens/Proposals/screens/ProposalsViewInfo/sections/LimitDetails";
import { ActionSheet } from "@repo/core/components/layout/Actionsheet";
import { ProposalsOptions } from "@repo/core/screens/Proposals/screens/ProposalsViewInfo/components/ProposalOptions";
import { ProposalOptionsItem } from "@repo/core/screens/Proposals/screens/ProposalsSearch/components/ProposalOptionsSheet/ProposalOptionsItem";
import { useProposalsViewInfoController } from "@repo/core/screens/Proposals/screens/ProposalsViewInfo/controller";
import { Text } from "@repo/core/components/core/Text";
import { StatusBarVariantEnum } from "@repo/core/components/layout/StatusBar/types";
import { getAnalysisQueueProposalOptions } from "@repo/core/screens/Proposals/rules/analysisQueueRules";
import { ScrollView } from "@repo/core/components/layout/ScrollView";
import { PageScaffold } from "@repo/core/components/layout/Scaffolds/PageScaffold";
import { DocumentPreviewBottomSheet } from "./components/DocumentPreviewBottomSheet";
import FixedLoading from "@repo/core/components/core/FixedLoading";
import Spinner from "@repo/core/components/core/Spinner";
import {
  AnalysisTabSkeleton,
  DocumentsListSkeleton,
  ProposalDataTabSkeleton,
  ProposalsViewInfoLoading
} from "./loading";
import { Platform } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { getFooterBottomPadding } from "@repo/core/components/layout/Scaffolds/components/ScaffoldFooter/utils";

const ProposalsViewInfo = () => {
  const insets = useSafeAreaInsets();
  const {
    isMobile,
    isWebDevice,
    alertModalRef,
    onPressEffectiveProposal,
    onPressOpenOptions,
    alertAnalysisModalRef,
    actionSheetRef,
    onSeeLimit,
    handleOnSeeMoreDetails,
    onCloseSeeLimit,
    handleOnPressOption,
    handleBack,
    proposalDetails,
    isLoadingProposal,
    isLoadingAnalysis,
    isLoadingDocuments,
    hasError,
    documents,
    handleViewDocument,
    documentPreviewBottomSheetRef,
    proposalType,
    isEffectuatingProposal,
    loadingOptionAction,
    onRefresh,
    refreshing,
    permissions,
    canViewDocuments
  } = useProposalsViewInfoController();
  const canViewAnalysisItems = canViewProposalAnalysisItems(
    proposalDetails?.statusCode,
    permissions
  );
  const canViewDataTab = canViewProposalDataTab(
    proposalDetails?.statusCode,
    permissions
  );
  const tabs = getProposalViewInfoTabs(
    isMobile,
    canViewDataTab,
    canViewDocuments
  );
  const fullWidthContentStyle = isWebDevice
    ? {
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        alignSelf: "stretch" as const
      }
    : undefined;
  const isScreenLoading = isLoadingProposal;
  const ContentWrapper = isWebDevice ? VStack : ScrollView;
  const contentWrapperProps = isWebDevice
    ? {
        flex: 1,
        w: "$full",
        bgColor: "$backgroundLight0",
        style: fullWidthContentStyle
      }
    : {
        bgColor: "$backgroundLight0",
        w: "$full",
        contentContainerStyle: {
          flexGrow: 1,
          width: "100%"
        },
        refreshControl: (
          <RefreshControl
            bg="$primary700"
            refreshing={refreshing && !isScreenLoading}
            onRefresh={onRefresh}
          />
        )
      };
  const footerBottomPadding = getFooterBottomPadding(
    Platform.OS,
    insets.bottom
  );

  return (
    <>
      {loadingOptionAction ? <FixedLoading /> : null}
      <PageScaffold
        showBackButtonOnWeb
        title="Detalhes da Proposta"
        isFullWidthContent
        statusBarVariant={StatusBarVariantEnum.LIGHT}
        onBackPress={handleBack}
        keyboardAware={false}
      >
        <ContentWrapper {...contentWrapperProps}>
          <VStack
            flex={1}
            w="$full"
            style={fullWidthContentStyle}
            pb={isMobile && isWebDevice ? "$32" : "$0"}
          >
            {isScreenLoading ? (
              <ProposalsViewInfoLoading />
            ) : hasError ? (
              <VStack
                flex={1}
                alignItems="center"
                justifyContent="center"
                h="$96"
                p="$6"
              >
                <Text fontSize="$md" color="$error600" textAlign="center">
                  Erro ao carregar os detalhes da proposta. Tente novamente.
                </Text>
              </VStack>
            ) : (
              <>
                {isMobile && proposalDetails && (
                  <VStack
                    bgColor="$backgroundLight0"
                    mb="$4"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Badge
                      action="muted"
                      borderRadius="$sm"
                      size="md"
                      w="$fit-content"
                      bgColor="$backgroundLight100"
                      py="$1"
                      px="$2"
                    >
                      <Text
                        h={22}
                        fontWeight={500}
                        fontSize="$md"
                        alignItems="center"
                        display="flex"
                        justifyContent="center"
                        color="$backgroundLight950"
                      >
                        {proposalDetails.proposalNumber}
                      </Text>
                    </Badge>
                  </VStack>
                )}
                <Tabs tabs={tabs} initialTab={0}>
                  {canViewDataTab ? (
                    <VStack w="$full" style={fullWidthContentStyle}>
                      {isMobile ? (
                        <CardInfoView
                          title="Dados da Proposta"
                          subTitle={proposalDetails?.proposalNumber || ""}
                          justifyContent="flex-start"
                        >
                          {isLoadingProposal ? (
                            <ProposalDataTabSkeleton />
                          ) : (
                            <ProposalDetails
                              onSeeLimits={onSeeLimit}
                              onSeeMore={handleOnSeeMoreDetails}
                              standardRejectionReasons={
                                proposalDetails?.standardRejectionReasons
                              }
                              clientName={proposalDetails?.clientName}
                              cpf={proposalDetails?.cpf}
                              proposalDate={proposalDetails?.proposalDate}
                              productCode={proposalDetails?.productCode}
                              productDescription={
                                proposalDetails?.productDescription
                              }
                              pointOfService={proposalDetails?.pointOfService}
                              status={proposalDetails?.status}
                              statusCode={proposalDetails?.statusCode}
                              statusDescription={
                                proposalDetails?.statusDescription
                              }
                              proposalType={proposalType}
                            />
                          )}
                        </CardInfoView>
                      ) : (
                        <>
                          <HStack
                            gap="$6"
                            w="$full"
                            flex={1}
                            alignItems="flex-start"
                          >
                            <CardInfoView
                              flex={canViewDocuments ? 3 : 1}
                              minWidth={0}
                              title="Dados da Proposta"
                              subTitle={proposalDetails?.proposalNumber || ""}
                              justifyContent="flex-start"
                            >
                              {isLoadingProposal ? (
                                <ProposalDataTabSkeleton />
                              ) : (
                                <ProposalDetails
                                  onSeeLimits={onSeeLimit}
                                  onSeeMore={handleOnSeeMoreDetails}
                                  standardRejectionReasons={
                                    proposalDetails?.standardRejectionReasons
                                  }
                                  clientName={proposalDetails?.clientName}
                                  cpf={proposalDetails?.cpf}
                                  proposalDate={proposalDetails?.proposalDate}
                                  productCode={proposalDetails?.productCode}
                                  productDescription={
                                    proposalDetails?.productDescription
                                  }
                                  pointOfService={
                                    proposalDetails?.pointOfService
                                  }
                                  status={proposalDetails?.status}
                                  statusCode={proposalDetails?.statusCode}
                                  statusDescription={
                                    proposalDetails?.statusDescription
                                  }
                                  proposalType={proposalType}
                                />
                              )}
                            </CardInfoView>
                            {canViewDocuments ? (
                              <VStack flex={1}>
                                <CardInfoView
                                  height="$fit-content"
                                  backgroundColor="$backgroundLightMuted"
                                  title="Documentos"
                                  borderWidth={0}
                                >
                                  {isLoadingDocuments ? (
                                    <DocumentsListSkeleton />
                                  ) : (
                                    <DocumentsView
                                      documents={documents}
                                      onViewDocument={handleViewDocument}
                                    />
                                  )}
                                </CardInfoView>
                                <VStack flex={1} pt="$1/4">
                                  <ProposalsOptions
                                    onPressOptions={onPressOpenOptions}
                                    onPressEffectiveProposal={
                                      onPressEffectiveProposal
                                    }
                                    proposalStatus={proposalDetails?.statusCode}
                                    proposalType={proposalType}
                                    permissions={permissions}
                                    isLoading={isEffectuatingProposal}
                                  />
                                </VStack>
                              </VStack>
                            ) : null}
                          </HStack>
                          {!canViewDocuments ? (
                            <VStack alignItems="flex-end" mt="$6">
                              <ProposalsOptions
                                onPressOptions={onPressOpenOptions}
                                onPressEffectiveProposal={
                                  onPressEffectiveProposal
                                }
                                proposalStatus={proposalDetails?.statusCode}
                                proposalType={proposalType}
                                permissions={permissions}
                                isLoading={isEffectuatingProposal}
                              />
                            </VStack>
                          ) : null}
                        </>
                      )}
                    </VStack>
                  ) : null}
                  {isMobile ? (
                    <VStack w="$full" style={fullWidthContentStyle}>
                      <CardInfoView
                        w="$full"
                        title="Dados da Proposta"
                        subTitle={proposalDetails?.proposalNumber || ""}
                        justifyContent="flex-start"
                      >
                        {isLoadingAnalysis ? (
                          <AnalysisTabSkeleton />
                        ) : (
                          <ProposalDetails
                            showAnalytics
                            showAnalyticsDetails={canViewAnalysisItems}
                            onSeeLimits={onSeeLimit}
                            proposalAnalytics={proposalDetails?.analysisItems}
                            standardRejectionReasons={
                              proposalDetails?.standardRejectionReasons
                            }
                            onSeeMore={handleOnSeeMoreDetails}
                            clientName={proposalDetails?.clientName}
                            cpf={proposalDetails?.cpf}
                            proposalDate={proposalDetails?.proposalDate}
                            productCode={proposalDetails?.productCode}
                            productDescription={
                              proposalDetails?.productDescription
                            }
                            pointOfService={proposalDetails?.pointOfService}
                            status={proposalDetails?.status}
                            statusCode={proposalDetails?.statusCode}
                            statusDescription={
                              proposalDetails?.statusDescription
                            }
                            proposalType={proposalType}
                          />
                        )}
                      </CardInfoView>
                    </VStack>
                  ) : (
                    <VStack w="$full" style={fullWidthContentStyle}>
                      <HStack
                        gap="$6"
                        w="$full"
                        flex={1}
                        alignItems="flex-start"
                      >
                        <CardInfoView
                          flex={canViewDocuments ? 3 : 1}
                          minWidth={0}
                          title="Dados da Proposta"
                          subTitle={proposalDetails?.proposalNumber || ""}
                          justifyContent="flex-start"
                        >
                          {isLoadingAnalysis ? (
                            <AnalysisTabSkeleton />
                          ) : (
                            <ProposalDetails
                              showAnalytics
                              showAnalyticsDetails={canViewAnalysisItems}
                              onSeeLimits={onSeeLimit}
                              proposalAnalytics={proposalDetails?.analysisItems}
                              standardRejectionReasons={
                                proposalDetails?.standardRejectionReasons
                              }
                              onSeeMore={handleOnSeeMoreDetails}
                              clientName={proposalDetails?.clientName}
                              cpf={proposalDetails?.cpf}
                              proposalDate={proposalDetails?.proposalDate}
                              productCode={proposalDetails?.productCode}
                              productDescription={
                                proposalDetails?.productDescription
                              }
                              pointOfService={proposalDetails?.pointOfService}
                              status={proposalDetails?.status}
                              statusCode={proposalDetails?.statusCode}
                              statusDescription={
                                proposalDetails?.statusDescription
                              }
                              proposalType={proposalType}
                            />
                          )}
                        </CardInfoView>
                        {canViewDocuments ? (
                          <VStack flex={1}>
                            <CardInfoView
                              height="$fit-content"
                              backgroundColor="$backgroundLightMuted"
                              title="Documentos"
                              borderWidth={0}
                            >
                              {isLoadingDocuments ? (
                                <DocumentsListSkeleton />
                              ) : (
                                <DocumentsView
                                  documents={documents}
                                  onViewDocument={handleViewDocument}
                                />
                              )}
                            </CardInfoView>
                            <VStack flex={1} pt="$1/4">
                              <ProposalsOptions
                                onPressOptions={onPressOpenOptions}
                                onPressEffectiveProposal={
                                  onPressEffectiveProposal
                                }
                                proposalStatus={proposalDetails?.statusCode}
                                proposalType={proposalType}
                                permissions={permissions}
                                isLoading={isEffectuatingProposal}
                              />
                            </VStack>
                          </VStack>
                        ) : null}
                      </HStack>
                      {!canViewDocuments ? (
                        <VStack alignItems="flex-end" mt="$6">
                          <ProposalsOptions
                            onPressOptions={onPressOpenOptions}
                            onPressEffectiveProposal={onPressEffectiveProposal}
                            proposalStatus={proposalDetails?.statusCode}
                            proposalType={proposalType}
                            permissions={permissions}
                            isLoading={isEffectuatingProposal}
                          />
                        </VStack>
                      ) : null}
                    </VStack>
                  )}
                  {isMobile && canViewDocuments ? (
                    <VStack w="$full" gap="$6" style={fullWidthContentStyle}>
                      <CardInfoView
                        w="$full"
                        flex={2}
                        backgroundColor="$backgroundLightMuted"
                        title="Documentos"
                        borderWidth={0}
                      >
                        {isLoadingDocuments ? (
                          <DocumentsListSkeleton />
                        ) : (
                          <DocumentsView
                            documents={documents}
                            onViewDocument={handleViewDocument}
                          />
                        )}
                      </CardInfoView>
                    </VStack>
                  ) : null}
                </Tabs>
              </>
            )}
          </VStack>
        </ContentWrapper>
      </PageScaffold>
      {isMobile && !hasError && !isScreenLoading && proposalDetails && (
        <VStack
          //@ts-ignore next-line -> Glustack no provide fixed on position props but working
          position="fixed"
          bottom={0}
          w="$full"
          px="$4"
          pt="$4"
          pb={footerBottomPadding}
          bg="$white"
        >
          <ProposalsOptions
            onPressOptions={onPressOpenOptions}
            onPressEffectiveProposal={onPressEffectiveProposal}
            proposalStatus={proposalDetails.statusCode}
            proposalType={proposalType}
            permissions={permissions}
            isLoading={isEffectuatingProposal}
          />
        </VStack>
      )}
      <AlertModal
        maxWidth={460}
        ref={alertModalRef}
        headerTitle="Limites"
        headerIcon="bars-progress"
        headerIconColor="$textLight900"
      >
        <LimitDetails
          purchases={proposalDetails?.limits?.purchases}
          withdrawals={proposalDetails?.limits?.withdrawals}
          installment={proposalDetails?.limits?.installment}
          monthlyInstallment={proposalDetails?.limits?.monthlyInstallment}
        />
        {!isMobile ? (
          <VStack alignItems={"flex-end"} mt="$8" mb="$3">
            <Button text="Fechar" size="sm" onPress={onCloseSeeLimit} />
          </VStack>
        ) : null}
      </AlertModal>
      <ActionSheet
        ref={actionSheetRef}
        backdropCanClose={!isEffectuatingProposal}
      >
        {isEffectuatingProposal ? (
          <VStack w="$full" p="$6" gap="$4" alignItems="center">
            <Spinner />
            <VStack gap="$1" alignItems="center">
              <Text fontSize="$md" fontWeight={600} color="$textLight900">
                Efetivando proposta
              </Text>
              <Text fontSize="$sm" color="$textLight600" textAlign="center">
                Aguarde enquanto processamos a efetivação.
              </Text>
            </VStack>
          </VStack>
        ) : (
          <VStack w="$full" p="$2" mb="$2">
            <VStack mb="$4" px="$3">
              <Text fontSize="$md" fontWeight={500} color="$textLight600">
                Opções
              </Text>
            </VStack>

            <VStack>
              {getAnalysisQueueProposalOptions(
                proposalDetails?.statusCode,
                proposalType,
                undefined,
                permissions
              ).map((option) => (
                <ProposalOptionsItem
                  key={option.label}
                  {...option}
                  isDisabled={!!loadingOptionAction}
                  isLoading={loadingOptionAction === option.action}
                  onPress={handleOnPressOption}
                />
              ))}
            </VStack>
          </VStack>
        )}
      </ActionSheet>
      <AlertModal
        ref={alertAnalysisModalRef}
        title="Por favor aguarde"
        icon="clock"
        maxWidth={427}
        iconColor="$warning400"
        iconVariant="solid"
        showIcon
      >
        <VStack gap="$8">
          <Text
            color="$textLight900"
            fontWeight={400}
            size={"sm"}
            textAlign="center"
          >
            A proposta está em análise na mesa de crédito.{"\n"} Isso pode levar
            alguns minutos.
          </Text>
          <Button
            text="Ver proposta"
            mb="$4"
            action="primary"
            onPress={() => alertAnalysisModalRef.current?.close()}
          />
        </VStack>
      </AlertModal>
      <DocumentPreviewBottomSheet ref={documentPreviewBottomSheetRef} />
    </>
  );
};

export default ProposalsViewInfo;
